import cloneDeep from 'clone-deep'
import { InputsTable } from 'components/InputsTable'
import type { InputType } from 'config'
import { useEffect, useRef, useState } from 'react'
import { confirmable, createConfirmation, ReactConfirmProps } from 'react-confirm'
import { Modal } from 'stories/components'

export interface IInputsTableOption {
  title?: string
  inputs: Record<string, InputType>
  /** Initial rows; each object uses the same keys as your form fields. */
  rows?: Record<string, any>[]
  titleOkay?: string
  titleCancel?: string
  /** Which row fields to show as table columns, in order. */
  columnKeys: string[]
  /** Optional label per key; default is `inputs[key].title` or the key string. */
  columnHeaders?: Record<string, string>
  addRowTitle?: string
  editRowTitle?: string
  /** Message for the remove confirmation dialog. */
  confirmRemoveMessage?: string
}

interface InputsTableDialogProps extends ReactConfirmProps {
  options: IInputsTableOption
}

const InputsTableDialog = ({ show, proceed, options }: InputsTableDialogProps) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now())
  const {
    title,
    inputs,
    rows: initialRows = [],
    titleOkay = 'Okay',
    titleCancel,
    columnKeys,
    columnHeaders = {},
    addRowTitle = 'Add',
    editRowTitle = 'Edit',
    confirmRemoveMessage = 'Are you sure you want to remove this row?',
  } = options

  const [rows, setRows] = useState<Record<string, any>[]>(() => cloneDeep(initialRows))
  const openedThisSession = useRef(false)

  useEffect(() => {
    setLastUpdatedAt(Date.now())
  }, [show])

  useEffect(() => {
    if (show) {
      if (!openedThisSession.current) {
        setRows(cloneDeep(initialRows))
        openedThisSession.current = true
      }
    } else {
      openedThisSession.current = false
    }
  }, [show, initialRows])

  const handleOk = () => {
    proceed(cloneDeep(rows) as any)
  }

  return (
    <Modal
      button={<span></span>}
      title={title || 'Items'}
      titleOkay={titleOkay}
      titleCancel={titleCancel}
      init={false}
      isOpen={show}
      lastUpdatedAt={lastUpdatedAt}
      onOk={handleOk}
      onClose={() => proceed(false as any)}
      childLevel={1}
    >
      <div className="w-196 max-w-[calc(100vw-2rem)] text-sm">
        <InputsTable
          inputs={inputs}
          rows={rows}
          onUpdate={setRows}
          columnKeys={columnKeys}
          columnHeaders={columnHeaders}
          addRowTitle={addRowTitle}
          editRowTitle={editRowTitle}
          confirmRemoveMessage={confirmRemoveMessage}
          formChildLevel={2}
        />
      </div>
    </Modal>
  )
}

const inputsTableDlg = confirmable(InputsTableDialog)
const createInputsTableDlg = createConfirmation(inputsTableDlg) as any as (
  props: any,
) => Promise<Record<string, any>[] | false>

export const openInputsTableDlg = (options: IInputsTableOption) => createInputsTableDlg({ options })
